import moment from "moment";
import {NODE_TYPE_DATAPORTAL} from "../constants/nodes";
import {localizeI18nObj} from "./i18n";
import {getFilteredTreeWithPaths} from "./tree";
import {isValidUrl} from "./urls";

const METADATA_TYPE_SPOD = "spod_json";
const METADATA_TYPE_CKAN = "ckan_package_show";
const METADATA_TYPE_SDMX = "sdmx-json";
const METADATA_TYPE_DATAPORTAL = NODE_TYPE_DATAPORTAL;

/* SDMX utility functions */

function flattenMetadataAttributes(attributes, flat = {}) {
  for (const attr of attributes) {
    const {id, texts, attributeSet} = attr;

    if (!id) continue;

    const hasValue = !!texts && Object.keys(texts).length > 0;
    const hasChildren = attributeSet?.reportedAttributes?.length > 0;

    if (hasValue) {
      if (!flat[id]) {
        flat[id] = [texts];
      } else {
        flat[id].push(texts);
      }
    }

    if (!hasValue && hasChildren) {
      const children = attributeSet.reportedAttributes;

      const flatChildren = {};
      flattenMetadataAttributes(children, flatChildren);

      if (!flat[id]) {
        flat[id] = [flatChildren];
      } else {
        flat[id].push(flatChildren);
      }
    }
  }
  return flat;
}

function localizeValue(value, prefLang, langs) {
  if (!prefLang || !langs) {
    return value;
  } else {
    return localizeI18nObj(value, prefLang, langs);
  }
}

function getValue(flat, id, prefLang, langs) {
  const val = flat?.[id];
  if (!val) return null;

  return localizeValue(val[0], prefLang, langs);
}

function getValues(flat, id, prefLang, langs) {
  const val = flat?.[id];
  if (!val) return [];

  return val.map(v => localizeValue(v, prefLang, langs)).filter(Boolean);
}

/* CKAN utility functions */

const normalizeReferenceDocumentation = refDoc => {
  if (Array.isArray(refDoc)) {
    return refDoc.filter(v => typeof v === "string");
  }
  if (typeof refDoc === "string") {
    return [refDoc];
  }
  return [];
};

/* Dataportal utility functions */

function getSelectedHierarchy(selectedGuids, list, prefLang) {
  const selectedArray = Array.isArray(selectedGuids) ? selectedGuids : [selectedGuids];
  const selectedSet = new Set(selectedArray);

  // 1. Normalize nodes
  const nodeMap = {};
  list.forEach(item => {
    nodeMap[item.__Guid] = {
      guid: item.__Guid,
      parent: item.parent || null,
      identifier: item.title?.[prefLang] || "",
      children: []
    };
  });

  // 2. Build tree
  const roots = [];
  Object.values(nodeMap).forEach(node => {
    if (node.parent && nodeMap[node.parent]) {
      nodeMap[node.parent].children.push(node);
    } else {
      roots.push(node);
    }
  });

  return getFilteredTreeWithPaths(roots, "children", node => selectedSet.has(node.guid));
}

/* Parsers */

const ckanJsonParse = (metadata, dateFormat, prefLang, langs) => {
  const themes = metadata.themes_aggregate?.length ? JSON.parse(metadata.themes_aggregate) : null;
  const themeTree = [];
  (themes || []).forEach(theme => {
    const children = theme.subthemes.map(subtheme => ({identifier: subtheme, children: []}));
    themeTree.push({
      identifier: theme.theme,
      children: children
    });
  });

  (metadata?.groups || []).forEach(group =>
    themeTree.push({identifier: group.display_name || group.title || group.name})
  );

  const temporalCoverage = metadata.temporal_coverage?.length ? JSON.parse(metadata.temporal_coverage)[0] : null;

  const creator = metadata.creator?.length ? JSON.parse(metadata.creator)[0] : null;

  const altIdentifier = metadata.alternate_identifier?.length ? JSON.parse(metadata.alternate_identifier)[0] : null;

  const languages = metadata.language?.length
    ? metadata.language.replaceAll("{", "").replaceAll("}", "").split(",")
    : null;

  let conformsTo;
  try {
    const conformsToParsed = JSON.parse(metadata.conforms_to || "[]");
    conformsTo = conformsToParsed.map(ct => ({
      identifier: ct.identifier,
      title: localizeI18nObj(ct.title, prefLang, langs),
      description: localizeI18nObj(ct.description, prefLang, langs),
      referenceDocumentation: normalizeReferenceDocumentation(ct.referenceDocumentation)
    }));
  } catch (e) {
    conformsTo = [];
  }

  let spatialCoverage;
  try {
    spatialCoverage = (metadata?.geographical_name || "")
      .replaceAll("{", "")
      .replaceAll("}", "")
      .split(",")
      .map(n => ({name: n}));
  } catch (e) {
    spatialCoverage = [];
  }

  const extras = (metadata?.extras || [])
    .filter(({key}) => key !== "theme")
    .map(extra => ({
      id: extra.key,
      label: extra.key,
      value: extra.value,
      link: isValidUrl(extra.value) ? extra.value : null
    }));

  return {
    identifier: metadata.identifier,
    title: metadata.title,
    description: metadata.notes,
    modified: metadata.modified ? moment(metadata.modified, "DD-MM-YYYY").format(dateFormat) : null,
    theme: themeTree,
    rightsHolder: {
      identifier: metadata.holder_identifier,
      name: metadata.holder_name
    },
    accrualPeriodicity: metadata.frequency,
    contactPoints: [
      {
        phone: metadata.maintainer_phone,
        email: metadata.maintainer_email,
        website: metadata.maintainer_website
      }
    ],
    publisher: {
      identifier: metadata.publisher_identifier,
      name: metadata.publisher_name
    },
    creators: [
      {
        identifier: creator?.creator_identifier,
        name: creator?.creator_name?.[prefLang]
      }
    ],
    versionInfo: metadata.version,
    issued: metadata.issued ? moment(metadata.issued, "DD-MM-YYYY").format(dateFormat) : null,
    languages: languages,
    keywords: metadata.keywords,
    versionOf: metadata.is_version_of,
    temporalCoverage: [
      {
        from: temporalCoverage?.temporal_start,
        to: temporalCoverage?.temporal_end
      }
    ],
    spatialCoverage: spatialCoverage,
    conformsTo: conformsTo,
    altIdentifier: altIdentifier?.identifier,
    resources: metadata.resources,
    license: metadata.license,
    extra: [...extras, ...(metadata?.extra || [])]
  };
};

const spodJsonParse = (metadata, dateFormat) => {
  const {ThemeHierarchy} = metadata;
  const themeNodes = {};
  const themeTree = [];
  Object.values(ThemeHierarchy).forEach(({Id, TermName}) => {
    themeNodes[Id] = {identifier: TermName, children: []};
  });
  Object.values(ThemeHierarchy).forEach(({Id, ParentId}) => {
    if (!ParentId) {
      themeTree.push(themeNodes[Id]);
    } else {
      themeNodes[ParentId].children.push(themeNodes[Id]);
    }
  });

  return {
    // identifier: metadata.DatasetIdentifier,
    // altIdentifier: metadata.OtherIdentifiers.join(", "),
    title: metadata.Title,
    description: metadata.Description,
    theme: themeTree,
    issued: metadata.CreationDate ? moment(metadata.CreationDate).format(dateFormat) : null,
    modified: metadata.LastUpdate ? moment(metadata.LastUpdate).format(dateFormat) : null,
    spatialCoverage: [
      {
        name: metadata.GeoNamesURI
      }
    ],
    languages: metadata.Languages,
    keywords: metadata.Keywords.join(", "),
    temporalCoverage: [
      {
        from: metadata.TemporalCoverage?.temporal_start,
        to: metadata.TemporalCoverage?.temporal_end
      }
    ],
    accrualPeriodicity: metadata.Frequency,
    versionOf: metadata.VersionOf,
    // publisher: {
    //   identifier: metadata.Editor?.Code,
    //   name: metadata.Editor?.Name
    // },
    // rightsHolder: {
    //   identifier: metadata.RightsHolder?.Code,
    //   name: metadata.RightsHolder?.Name
    // },
    creators: [
      {
        identifier: metadata.Creator?.Code,
        name: metadata.Creator?.Name
      }
    ],
    contactPoints: [
      {
        email: metadata.Contact.Email,
        website: metadata.Contact.Website
      }
    ],
    resources: metadata.resources,
    license: metadata.license,
    extra: metadata.extra
  };
};

const sdmxJsonParse = (metadata, dateFormat, prefLang, langs) => {
  // checking if the MetadataSet is compatible by checking the referenced MSD
  const metadataSet = (metadata?.data?.metadataSets || []).find(metadataSet =>
    (metadataSet?.links ?? []).find(link => (link?.urn ?? "").includes("IT1:DCAT-AP_IT_MSD"))
  );
  if (!metadataSet) return null;

  // selecting (the first) dataflow report
  const dataflowReport = (metadataSet.reports || []).find(report => report?.target?.id === "DATAFLOW_TARGET_ID");
  if (!dataflowReport) return null;

  const datasetAttr = (dataflowReport.attributeSet?.reportedAttributes || []).find(
    attr => attr?.id === "DCAT_AP_IT_DATASET"
  );
  if (!datasetAttr?.attributeSet?.reportedAttributes) return null;

  const flat = flattenMetadataAttributes(datasetAttr.attributeSet.reportedAttributes);

  const rightsHolder = getValue(flat, "DCAT_AP_IT_DATASET_RIGHTS_HOLDER");
  const distributions = getValues(flat, "DCAT_AP_IT_DATASET_DISTRIBUTION");
  const contactPoints = getValues(flat, "DCAT_AP_IT_DATASET_CONTACT_POINT");
  const publisher = getValue(flat, "DCAT_AP_IT_DATASET_PUBLISHER");
  const creators = getValues(flat, "DCAT_AP_IT_DATASET_CREATOR");
  const temporalCoverage = getValues(flat, "DCAT_AP_IT_DATASET_TEMPORAL");
  const spatialCoverage = getValues(flat, "DCAT_AP_IT_DATASET_SPATIAL");
  const conformsTo = getValues(flat, "DCAT_AP_IT_DATASET_CONFORMS_TO");
  const altIdentifier = getValues(flat, "DCAT_AP_IT_DATASET_OTHER_IDENTIFIER");

  return {
    identifier: getValue(flat, "DCAT_AP_IT_DATASET_IDENTIFIER", prefLang, langs),
    title: getValue(flat, "DCAT_AP_IT_DATASET_TITLE", prefLang, langs),
    description: getValue(flat, "DCAT_AP_IT_DATASET_DESCRIPTION", prefLang, langs),
    modified: flat["DCAT_AP_IT_DATASET_MODIFIED"]
      ? moment(getValue(flat, "DCAT_AP_IT_DATASET_MODIFIED", prefLang, langs)).format(dateFormat)
      : null,
    theme: getValues(flat, "DCAT_AP_IT_DATASET_THEME", prefLang, langs).map(item => ({identifier: item})),
    rightsHolder: {
      identifier: getValue(rightsHolder, "DCAT_AP_IT_DATASET_RIGHTS_HOLDER_AGENT_IDENTIFIER", prefLang, langs),
      name: getValue(rightsHolder, "DCAT_AP_IT_DATASET_RIGHTS_HOLDER_AGENT_NAME", prefLang, langs)
    },
    accrualPeriodicity: getValue(flat, "DCAT_AP_IT_DATASET_ACCRUAL_PERIODICITY", prefLang, langs),
    distributions: distributions.map(d => {
      const license = getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_LICENSE");
      return {
        title: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_TITLE", prefLang, langs),
        description: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_DESCRIPTION", prefLang, langs),
        format: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_FORMAT", prefLang, langs),
        accessUrl: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_ACCESS_URL", prefLang, langs),
        size: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_SIZE", prefLang, langs),
        lastModified: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_LAST_MODIFIED", prefLang, langs),
        downloadUrl: getValue(d, "DCAT_AP_IT_DATASET_DISTRIBUTION_DOWNLOAD_URL", prefLang, langs),
        license: {
          name: getValue(license, "DCAT_AP_IT_DATASET_DISTRIBUTION_LICENSE_NAME", prefLang, langs),
          type: getValue(license, "DCAT_AP_IT_DATASET_DISTRIBUTION_LICENSE_TYPE", prefLang, langs),
          version: getValue(license, "DCAT_AP_IT_DATASET_DISTRIBUTION_LICENSE_VERSION", prefLang, langs)
        }
      };
    }),
    contactPoints: contactPoints.map(cp => ({
      name: getValue(cp, "DCAT_AP_IT_CONTACT_POINT_NAME", prefLang, langs),
      email: getValue(cp, "DCAT_AP_IT_CONTACT_POINT_MAIL", prefLang, langs),
      phone: getValue(cp, "DCAT_AP_IT_CONTACT_POINT_PHONE", prefLang, langs),
      website: getValue(cp, "DCAT_AP_IT_CONTACT_POINT_SITE", prefLang, langs)
    })),
    publisher: {
      identifier: getValue(publisher, "DCAT_AP_IT_DATASET_PUBLISHER_AGENT_IDENTIFIER", prefLang, langs),
      name: getValue(publisher, "DCAT_AP_IT_DATASET_PUBLISHER_AGENT_NAME", prefLang, langs)
    },
    creators: creators.map(creator => ({
      identifier: getValue(creator, "DCAT_AP_IT_DATASET_CREATOR_AGENT_IDENTIFIER", prefLang, langs),
      name: getValue(creator, "DCAT_AP_IT_DATASET_CREATOR_AGENT_NAME", prefLang, langs)
    })),
    versionInfo: getValue(flat, "DCAT_AP_IT_DATASET_VERSIONINFO", prefLang, langs),
    issued: flat["DCAT_AP_IT_DATASET_ISSUED"]
      ? moment(getValue(flat, "DCAT_AP_IT_DATASET_ISSUED", prefLang, langs)).format(dateFormat)
      : null,
    landingPage: getValue(flat, "DCAT_AP_IT_DATASET_LANDING_PAGE", prefLang, langs),
    languages: getValues(flat, "DCAT_AP_IT_DATASET_LANGUAGE", prefLang, langs),
    keywords: getValues(flat, "DCAT_AP_IT_DATASET_KEYWORD", prefLang, langs).join(", "),
    versionOf: getValues(flat, "DCAT_AP_IT_DATASET_VERSION_OF", prefLang, langs).join(", "),
    temporalCoverage: temporalCoverage.map(tc => ({
      from: getValue(tc, "DCAT_AP_IT_PERIOD_OF_TIME_START_DATE", prefLang, langs),
      to: getValue(tc, "DCAT_AP_IT_PERIOD_OF_TIME_END_DATE", prefLang, langs)
    })),
    spatialCoverage: spatialCoverage.map(sc => {
      const geometry = getValue(sc, "DCAT_AP_IT_DATASET_SPATIAL_GEOMETRY");
      return {
        name: getValue(sc, "DCAT_AP_IT_DATASET_SPATIAL_GEO_NAME", prefLang, langs),
        geometry: {
          type: getValue(geometry, "DCAT_AP_IT_DATASET_SPATIAL_GEOMETRY_TYPE", prefLang, langs),
          coordinates: getValue(geometry, "DCAT_AP_IT_DATASET_SPATIAL_GEOMETRY_COORDINATES", prefLang, langs),
          crs: getValue(geometry, "DCAT_AP_IT_DATASET_SPATIAL_GEOMETRY_CRS", prefLang, langs)
        }
      };
    }),
    conformsTo: conformsTo.map(ct => ({
      identifier: getValue(ct, "DCAT_AP_IT_STANDARD_IDENTIFIER", prefLang, langs),
      title: getValue(ct, "DCAT_AP_IT_STANDARD_TITLE", prefLang, langs),
      description: getValue(ct, "DCAT_AP_IT_STANDARD_DESCRIPTION", prefLang, langs),
      referenceDocumentation: getValues(ct, "DCAT_AP_IT_STANDARD_REFERENCE_DOCUMENTATION", prefLang, langs)
    })),
    altIdentifier: altIdentifier
      .map(ai => getValues(ai, "DCAT_AP_IT_ALTERNATIVE_IDENTIFIER_NOTATION", prefLang, langs).join(", "))
      .join(", "),
    license: metadata.license,
    extra: metadata.extra
  };
};

const dataportalJsonParse = (metadata, dateFormat, prefLang, langs, schema) => {
  const themesList = schema.Themes?.items?.elements || [];
  const licenseList = schema.License?.elements || [];
  const keywordsList = metadata.Keywords?.[prefLang] || [];
  const sourceList = schema.Source?.elements || [];
  const accrualPeriodicityList = schema.AccrualPeriodicity?.elements || [];

  const themes = getSelectedHierarchy(metadata.Themes, themesList, prefLang);
  const license = getSelectedHierarchy(metadata.License, licenseList, prefLang);
  const keywords = keywordsList.join(", ");
  const source = sourceList.find(source => source.__Guid === metadata.Source);
  const accrualPeriodicity = accrualPeriodicityList.find(
    periodicity => periodicity.__Guid === metadata.AccrualPeriodicity
  );

  return {
    identifier: metadata.Identifier,
    title: localizeI18nObj(metadata.Names, prefLang, langs),
    description: localizeI18nObj(metadata.Descriptions, prefLang, langs),
    modified: moment(metadata.LastUpdate).format(dateFormat),
    source: localizeI18nObj(source?.title, prefLang, langs),
    accrualPeriodicity: localizeI18nObj(accrualPeriodicity?.title, prefLang, langs),
    theme: themes,
    keywords: keywords,
    license: license,
    resources: metadata.resources,
    extra: metadata.extra
  };
};

export const parseJsonMetadata = (metadata, prefLang, langs, dateFormat, schema) => {
  let parsedMetadata = null;
  try {
    switch (metadata.type) {
      case METADATA_TYPE_SPOD:
        parsedMetadata = spodJsonParse(metadata.data, dateFormat);
        break;
      case METADATA_TYPE_CKAN:
        parsedMetadata = ckanJsonParse(metadata.data, dateFormat, prefLang, langs);
        break;
      case METADATA_TYPE_SDMX:
        parsedMetadata = sdmxJsonParse(metadata.data, dateFormat, prefLang, langs);
        break;
      case METADATA_TYPE_DATAPORTAL:
        parsedMetadata = dataportalJsonParse(metadata.data, dateFormat, prefLang, langs, schema);
        break;
      default:
        break;
    }
  } catch (e) {
    parsedMetadata = null;
  }
  return parsedMetadata;
};
